import React, { useState, useMemo, useEffect } from 'react';
import { 
  Card, 
  Typography, 
  Input, 
  Button, 
  Space, 
  Spin, 
  Form, 
  message,
  Divider,
  Tag,
  Select
} from 'antd';
import { useLocation } from 'react-router-dom';
import { useUserProfile, updateUserProfile, updateUserPassword, useUserAnswersDashboard, useUserServiceDetails, useCurrentUser } from '../hooks/user';
import { useCourseDetails } from '../hooks/course';
import { useResponsive } from '../hooks/useResponsive';
import { useSecurityProtection } from '../hooks/useSecurityProtection';
import authService from '../services/authService';
import { LinkFacebookButton } from '../components/auth/FacebookLoginButton';

const { Title, Text } = Typography;

const Profile: React.FC = () => {
  const { isMobile } = useResponsive();
  const location = useLocation();
  useSecurityProtection();
  const currentUser = authService.getCurrentUser();
  const { data: userProfile, isLoading, refetch } = useUserProfile(undefined, currentUser?.email);
  const { data: userServiceDetails, isLoading: isServiceLoading } = useUserServiceDetails(currentUser?.id);
  const { data: currentUserDetails, refetch: refetchCurrentUser } = useCurrentUser();
  
  // Extract course_id from user service details (response is an array)
  const userService = userServiceDetails?.[0];
  const courseId = userService?.course_id?.toString();
  console.log('Extracted courseId:', courseId);
  
  // Fetch course details using course_id
  const { data: courseDetails, isLoading: isCourseLoading } = useCourseDetails(undefined, courseId);
  console.log('Fetched course details:', courseDetails);

  
  const [personalInfoForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [isUpdatingPersonalInfo, setIsUpdatingPersonalInfo] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [activeSection, setActiveSection] = useState<'account' | 'quizDetails'>('account');
  
  // Handle navigation state for activeTab
  useEffect(() => {
    const state = location.state as { activeTab?: 'account' | 'quizDetails' } | null;
    if (state?.activeTab) {
      setActiveSection(state.activeTab);
    }
  }, [location.state]);

  const [selectedTopic, setSelectedTopic] = useState<string | undefined>(undefined);
  const [selectedLesson, setSelectedLesson] = useState<string | undefined>(undefined);

  // Quiz Details data
  const { data: quizDetailsResponse, isLoading: isQuizLoading } = useUserAnswersDashboard();
  
  // Extract data array from response
  const quizDetailsData = quizDetailsResponse?.data || quizDetailsResponse || [];

  // Debug user service details
  console.log('User Service Details:', userServiceDetails);

  // Extract unique topics and lessons from quiz data
  const { topics, lessons, filteredQuizData } = useMemo(() => {
    if (!quizDetailsData || !Array.isArray(quizDetailsData) || quizDetailsData.length === 0) {
      return { topics: [], lessons: [], filteredQuizData: [] };
    }

    const filteredData = quizDetailsData.filter((item: any) => item.course_id && item.course_id !== 0);
    
    // Extract unique topics
    const uniqueTopicTitles = Array.from(
      new Set(filteredData.map((item: any) => item.topic_title).filter(Boolean))
    );
    const uniqueTopics = uniqueTopicTitles.map((title) => ({
      value: title as string,
      label: title as string
    }));

    // Extract lessons based on selected topic
    let availableLessons = filteredData;
    if (selectedTopic) {
      availableLessons = filteredData.filter((item: any) => 
        item.topic_title === selectedTopic
      );
    }

    const lessonMap = new Map();
    availableLessons.forEach((item: any) => {
      if (item.lesson_id && item.lesson_title) {
        lessonMap.set(item.lesson_id, item.lesson_title);
      }
    });
    
    const uniqueLessons = Array.from(lessonMap.entries()).map(([id, title]) => ({
      value: id.toString(),
      label: `${title}`
    }));

    // Filter quiz data based on selected topic and lesson
    let finalFilteredData = filteredData;
    if (selectedTopic) {
      finalFilteredData = finalFilteredData.filter((item: any) => 
        item.topic_title === selectedTopic
      );
    }
    if (selectedLesson) {
      finalFilteredData = finalFilteredData.filter((item: any) => 
        item.lesson_id?.toString() === selectedLesson
      );
    }

    return { topics: uniqueTopics, lessons: uniqueLessons, filteredQuizData: finalFilteredData };
  }, [quizDetailsData, selectedTopic, selectedLesson]);

  const handleUpdatePersonalInfo = async (values: any) => {
    setIsUpdatingPersonalInfo(true);
    try {
      const email = currentUser?.email || userProfile?.email;
      if (!email) {
        throw new Error('No email found');
      }
      
      await updateUserProfile(email, {
        first_name: values.firstName,
        last_name: values.lastName
      });
      
      message.success('Personal information updated successfully');
      refetch();
    } catch (error: any) {
      message.error(error.message || 'Failed to update personal information');
    } finally {
      setIsUpdatingPersonalInfo(false);
    }
  };

  const handleUpdatePassword = async (values: any) => {
    setIsUpdatingPassword(true);
    try {
      await updateUserPassword({
        email: userProfile.email || currentUser?.email || '',
        password_hash: values.newPassword
      });
      
      message.success('Password updated successfully');
      passwordForm.resetFields();
    } catch (error: any) {
      message.error(error.message || 'Failed to update password');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleAddEmail = () => {
    // Logic to add another email
    message.info('Add email functionality would be implemented here');
  };

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '16px' : '24px',
      background: '#f8f9fa',
      minHeight: '100vh'
    }}>
      <div style={{ marginBottom: isMobile ? '24px' : '32px' }}>
        <Title level={2} style={{ 
          margin: 0, 
          fontSize: isMobile ? '20px' : '28px',
          fontWeight: '600'
        }}>
          Profile Settings
        </Title>
      </div>

      <div style={{ 
        display: 'flex', 
        gap: isMobile ? '16px' : '24px', 
        flexDirection: isMobile ? 'column' : 'row' 
      }}>
        {/* Left Sidebar Navigation */}
        <div style={{ 
          flex: isMobile ? '1' : '0 0 250px',
          marginBottom: isMobile ? '16px' : '0'
        }}>
          <Card
            style={{
              borderRadius: '12px',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              background: '#f8f9fa'
            }}
          >
            <div style={{ padding: isMobile ? '8px 0' : '12px 0' }}>
              <div 
                style={{
                  padding: isMobile ? '8px 12px' : '12px 16px',
                  background: activeSection === 'account' ? '#e9ecef' : 'transparent',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onClick={() => setActiveSection('account')}
                onMouseEnter={(e) => {
                  if (activeSection !== 'account') {
                    e.currentTarget.style.backgroundColor = '#f1f3f4';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeSection !== 'account') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <Text style={{
                  fontWeight: activeSection === 'account' ? '600' : '400',
                  fontSize: isMobile ? '14px' : '16px',
                  color: activeSection === 'account' ? '#262626' : '#6c757d'
                }}>
                  Account
                </Text>
              </div>
              
              <div 
                style={{
                  padding: isMobile ? '8px 12px' : '12px 16px',
                  background: activeSection === 'quizDetails' ? '#e9ecef' : 'transparent',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  transition: 'background-color 0.2s'
                }}
                onClick={() => setActiveSection('quizDetails')}
                onMouseEnter={(e) => {
                  if (activeSection !== 'quizDetails') {
                    e.currentTarget.style.backgroundColor = '#f1f3f4';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeSection !== 'quizDetails') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <Text style={{
                  fontWeight: activeSection === 'quizDetails' ? '600' : '400',
                  fontSize: isMobile ? '14px' : '16px',
                  color: activeSection === 'quizDetails' ? '#262626' : '#6c757d'
                }}>
                  Exam Details

                </Text>
              </div>
            </div>
          </Card>
        </div>

        {/* Right Content Area */}
        <div style={{ flex: 1 }}>
          {activeSection === 'account' ? (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* Personal Info Section */}
              <Card
          style={{
            borderRadius: '12px',
            border: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}
        >
          <Title level={3} style={{ 
            margin: '0 0 20px 0', 
            fontSize: isMobile ? '16px' : '20px',
            fontWeight: '600',
            color: '#262626'
          }}>
            Personal info
          </Title>

          {/* User Service Details */}
          {(isServiceLoading || isCourseLoading) ? (
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <Spin size="small" />
            </div>
          ) : (userService) ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: '16px',
              marginBottom: '20px'
            }}>
              {/* Course Card */}
              <Card style={{
                borderRadius: '8px',
                border: '1px solid #e9ecef',
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                background: '#f8f9fa'
              }}>
                <Text style={{ 
                  fontWeight: '500', 
                  fontSize: isMobile ? '12px' : '14px',
                  color: '#666',
                  display: 'block',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Course
                </Text>
                <Text style={{ 
                  fontSize: isMobile ? '16px' : '18px',
                  color: '#262626',
                  fontWeight: '600',
                  display: 'block'
                }}>
                  {courseDetails?.title || 'N/A'}
                </Text>
              </Card>

              {/* Expires At Card */}
              <Card style={{
                borderRadius: '8px',
                border: '1px solid #e9ecef',
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                background: '#f8f9fa'
              }}>
                <Text style={{ 
                  fontWeight: '500', 
                  fontSize: isMobile ? '12px' : '14px',
                  color: '#666',
                  display: 'block',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Expires At
                </Text>
                <Text style={{ 
                  fontSize: isMobile ? '16px' : '18px',
                  color: '#262626',
                  fontWeight: '600',
                  display: 'block'
                }}>
                  {userService.expired_at ? new Date(userService.expired_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'N/A'}
                </Text>
              </Card>
            </div>
          ) : null}

          <Form
            form={personalInfoForm}
            layout="vertical"
            onFinish={handleUpdatePersonalInfo}
            initialValues={{
              firstName: userProfile?.first_name || 'First name',
              lastName: userProfile?.last_name || 'Last name'
            }}
          >
            <Form.Item
              name="firstName"
              label={
                <Text style={{ 
                  fontWeight: '500', 
                  fontSize: isMobile ? '14px' : '16px',
                  color: '#262626'
                }}>
                  First name
                </Text>
              }
            >
              <Input
                size="large"
                style={{
                  borderRadius: '8px',
                  fontSize: isMobile ? '14px' : '16px',
                  padding: isMobile ? '8px 12px' : '12px 16px'
                }}
                placeholder="Enter your first name"
              />
            </Form.Item>

            <Form.Item
              name="lastName"
              label={
                <Text style={{ 
                  fontWeight: '500', 
                  fontSize: isMobile ? '14px' : '16px',
                  color: '#262626'
                }}>
                  Last name
                </Text>
              }
            >
              <Input
                size="large"
                style={{
                  borderRadius: '8px',
                  fontSize: isMobile ? '14px' : '16px',
                  padding: isMobile ? '8px 12px' : '12px 16px'
                }}
                placeholder="Enter your last name"
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              {/* <Button
                type="default"
                htmlType="submit"
                loading={isUpdatingPersonalInfo}
                style={{
                  borderRadius: '8px',
                  height: isMobile ? '40px' : '44px',
                  padding: '0 24px',
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: '500',
                  background: '#f5f5f5',
                  border: '1px solid #d9d9d9',
                  color: '#595959'
                }}
              >
                Update personal info
              </Button> */}
            </Form.Item>
          </Form>
        </Card>

        {/* Facebook Account Section */}
        {currentUserDetails && !currentUserDetails.facebook_linked && (
          <Card
            style={{
              borderRadius: '12px',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
            }}
          >
            <Title level={3} style={{
              margin: '0 0 8px 0',
              fontSize: isMobile ? '16px' : '20px',
              fontWeight: '600',
              color: '#262626'
            }}>
              Facebook акаунт
            </Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
              Дараагийн удаа Facebook-ээр шууд нэвтрэхийн тулд акаунтаа холбоно уу.
            </Text>
            <LinkFacebookButton
              onLinked={() => {
                message.success('Facebook акаунт амжилттай холбогдлоо');
                refetchCurrentUser();
              }}
              onError={(errorMessage) => {
                message.error(errorMessage);
              }}
            />
          </Card>
        )}

        {/* Email Address Section */}
        {/* <Card
          style={{
            borderRadius: '12px',
            border: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}
        >
          <Title level={3} style={{ 
            margin: '0 0 20px 0', 
            fontSize: isMobile ? '16px' : '20px',
            fontWeight: '600',
            color: '#262626'
          }}>
            Email address
          </Title>

          <div style={{
            border: '1px solid #f0f0f0',
            borderRadius: '8px',
            padding: isMobile ? '12px 16px' : '16px 20px',
            marginBottom: '16px',
            background: '#fafafa'
          }}>
            <div style={
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: isMobile ? 'wrap' : 'nowrap',
              gap: '8px'
            }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <Text style={{ 
                  fontSize: isMobile ? '14px' : '16px',
                  color: '#262626'
                }}>
                  {userProfile?.email || currentUser?.email || 'davka.est1996@gmail.com'}
                </Text>
              </div>
              <div style={{ 
                display: 'flex', 
                gap: '8px',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <Tag 
                  color="green" 
                  style={{ 
                    margin: 0,
                    fontSize: isMobile ? '11px' : '12px',
                    padding: '2px 8px',
                    borderRadius: '4px'
                  }}
                >
                  VERIFIED
                </Tag>
                <Tag 
                  color="blue" 
                  style={{ 
                    margin: 0,
                    fontSize: isMobile ? '11px' : '12px',
                    padding: '2px 8px',
                    borderRadius: '4px'
                  }}
                >
                  PRIMARY
                </Tag>
              </div>
            </div>
          </div>

          <Button
            type="default"
            onClick={handleAddEmail}
            style={{
              borderRadius: '8px',
              height: isMobile ? '40px' : '44px',
              padding: '0 24px',
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: '500',
              background: '#262626',
              border: '1px solid #262626',
              color: 'white',
              width: isMobile ? '100%' : 'auto'
            }}
          >
            Add another email
          </Button>
        </Card> */}

        {/* Change Password Section */}
        <Card
          style={{
            borderRadius: '12px',
            border: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}
        >
          <Title level={3} style={{ 
            margin: '0 0 20px 0', 
            fontSize: isMobile ? '16px' : '20px',
            fontWeight: '600',
            color: '#262626'
          }}>
            Change Password
          </Title>

          <Form
            form={passwordForm}
            layout="vertical"
            onFinish={handleUpdatePassword}
          >
            <Form.Item
              name="currentPassword"
              label={
                <Text style={{ 
                  fontWeight: '500', 
                  fontSize: isMobile ? '14px' : '16px',
                  color: '#262626'
                }}>
                  Current Password
                </Text>
              }
              rules={[
                { required: true, message: 'Please enter your current password' }
              ]}
            >
              <Input.Password
                size="large"
                style={{
                  borderRadius: '8px',
                  fontSize: isMobile ? '14px' : '16px'
                }}
                placeholder="Enter your current password"
              />
            </Form.Item>

            <Form.Item
              name="newPassword"
              label={
                <Text style={{ 
                  fontWeight: '500', 
                  fontSize: isMobile ? '14px' : '16px',
                  color: '#262626'
                }}>
                  New Password
                </Text>
              }
              rules={[
                { required: true, message: 'Please enter your new password' },
                { min: 6, message: 'Password must be at least 6 characters' }
              ]}
            >
              <Input.Password
                size="large"
                style={{
                  borderRadius: '8px',
                  fontSize: isMobile ? '14px' : '16px'
                }}
                placeholder="Enter your new password"
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label={
                <Text style={{ 
                  fontWeight: '500', 
                  fontSize: isMobile ? '14px' : '16px',
                  color: '#262626'
                }}>
                  Confirm New Password
                </Text>
              }
              dependencies={['newPassword']}
              rules={[
                { required: true, message: 'Please confirm your new password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password
                size="large"
                style={{
                  borderRadius: '8px',
                  fontSize: isMobile ? '14px' : '16px'
                }}
                placeholder="Confirm your new password"
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={isUpdatingPassword}
                style={{
                  borderRadius: '8px',
                  height: isMobile ? '40px' : '44px',
                  padding: '0 24px',
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: '500',
                  background: '#1890ff',
                  border: '1px solid #1890ff',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                Update Password
              </Button>
            </Form.Item>
          </Form>
        </Card>
          </Space>
          ) : (
            /* Quiz Details Section */
            <div style={{ width: '100%' }}>
              {isQuizLoading ? (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  minHeight: '300px' 
                }}>
                  <Spin size="large" />
                </div>
              ) : (
                <Card
                  style={{
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                  }}
                >
                  <Title level={3} style={{ 
                    margin: '0 0 20px 0', 
                    fontSize: isMobile ? '16px' : '20px',
                    fontWeight: '600',
                    color: '#262626'
                  }}>
                    Exam Details Dashboard
                  </Title>

                  {/* Filter Controls */}
                  <div style={{
                    marginBottom: '24px',
                    padding: '16px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef'
                  }}>
                    <Text strong style={{
                      fontSize: isMobile ? '14px' : '16px',
                      color: '#262626',
                      display: 'block',
                      marginBottom: '12px'
                    }}>
                      Filter by Topic and Lesson
                    </Text>
                    
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                      gap: '12px'
                    }}>
                      <div>
                        <Text style={{
                          fontSize: isMobile ? '12px' : '14px',
                          color: '#666',
                          display: 'block',
                          marginBottom: '4px'
                        }}>
                          Select Topic:
                        </Text>
                        <Select
                          style={{ width: '100%' }}
                          placeholder="All Topics"
                          allowClear
                          value={selectedTopic}
                          onChange={(value) => {
                            setSelectedTopic(value);
                            setSelectedLesson(undefined); // Reset lesson when topic changes
                          }}
                          options={[
                            { value: undefined, label: 'All Topics' },
                            ...topics
                          ]}
                        />
                      </div>
                      
                      <div>
                        <Text style={{
                          fontSize: isMobile ? '12px' : '14px',
                          color: '#666',
                          display: 'block',
                          marginBottom: '4px'
                        }}>
                          Select Lesson:
                        </Text>
                        <Select
                          style={{ width: '100%' }}
                          placeholder="All Lessons"
                          allowClear
                          value={selectedLesson}
                          onChange={setSelectedLesson}
                          disabled={!selectedTopic}
                          options={[
                            { value: undefined, label: 'All Lessons' },
                            ...lessons
                          ]}
                        />
                      </div>
                    </div>
                  </div>

                  {filteredQuizData && filteredQuizData.length > 0 ? (
                    <div>
                      {/* Summary Statistics */}
                      <Card style={{
                        background: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        borderRadius: '8px',
                        marginBottom: '24px'
                      }}>
                        <Title level={4} style={{ 
                          fontSize: isMobile ? '14px' : '16px',
                          color: '#262626',
                          margin: '0 0 16px 0'
                        }}>
                          Filtered Results Summary
                        </Title>
                        
                        {(() => {
                          // Calculate overall statistics for filtered data
                          if (!Array.isArray(filteredQuizData) || filteredQuizData.length === 0) {
                            return <div>No data available</div>;
                          }
                          
                          const totalQuestions = filteredQuizData.length;
                          const totalPoints = filteredQuizData.reduce((sum: number, item: any) => sum + (item.point || 0), 0);
                          const correctAnswers = filteredQuizData.filter((item: any) => item.point > 0).length;
                          const accuracy = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;
                          const uniqueTopics = new Set(filteredQuizData.map((item: any) => item.topic_title).filter(Boolean)).size;
                          const uniqueLessons = new Set(filteredQuizData.map((item: any) => item.lesson_id).filter(Boolean)).size;
                          
                          return (
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                              gap: '16px'
                            }}>
                              <div style={{ textAlign: 'center', padding: '12px', background: 'white', borderRadius: '6px' }}>
                                <div style={{
                                  fontSize: isMobile ? '20px' : '28px',
                                  fontWeight: 'bold',
                                  color: '#1890ff'
                                }}>
                                  {uniqueTopics}
                                </div>
                                <Text style={{ fontSize: isMobile ? '11px' : '13px', color: '#666' }}>
                                  Topics
                                </Text>
                              </div>
                              
                              <div style={{ textAlign: 'center', padding: '12px', background: 'white', borderRadius: '6px' }}>
                                <div style={{
                                  fontSize: isMobile ? '20px' : '28px',
                                  fontWeight: 'bold',
                                  color: '#722ed1'
                                }}>
                                  {uniqueLessons}
                                </div>
                                <Text style={{ fontSize: isMobile ? '11px' : '13px', color: '#666' }}>
                                  Lessons
                                </Text>
                              </div>
                              
                              <div style={{ textAlign: 'center', padding: '12px', background: 'white', borderRadius: '6px' }}>
                                <div style={{
                                  fontSize: isMobile ? '20px' : '28px',
                                  fontWeight: 'bold',
                                  color: '#fa541c'
                                }}>
                                  {totalQuestions}
                                </div>
                                <Text style={{ fontSize: isMobile ? '11px' : '13px', color: '#666' }}>
                                  Questions
                                </Text>
                              </div>
                              
                              <div style={{ textAlign: 'center', padding: '12px', background: 'white', borderRadius: '6px' }}>
                                <div style={{
                                  fontSize: isMobile ? '20px' : '28px',
                                  fontWeight: 'bold',
                                  color: '#52c41a'
                                }}>
                                  {totalPoints}
                                </div>
                                <Text style={{ fontSize: isMobile ? '11px' : '13px', color: '#666' }}>
                                  Total Points
                                </Text>
                              </div>
                              
                              <div style={{ textAlign: 'center', padding: '12px', background: 'white', borderRadius: '6px' }}>
                                <div style={{
                                  fontSize: isMobile ? '20px' : '28px',
                                  fontWeight: 'bold',
                                  color: '#52c41a'
                                }}>
                                  {correctAnswers}
                                </div>
                                <Text style={{ fontSize: isMobile ? '11px' : '13px', color: '#666' }}>
                                  Correct
                                </Text>
                              </div>
                              
                              <div style={{ textAlign: 'center', padding: '12px', background: 'white', borderRadius: '6px' }}>
                                <div style={{
                                  fontSize: isMobile ? '20px' : '28px',
                                  fontWeight: 'bold',
                                  color: accuracy >= 70 ? '#52c41a' : accuracy >= 50 ? '#faad14' : '#ff4d4f'
                                }}>
                                  {accuracy}%
                                </div>
                                <Text style={{ fontSize: isMobile ? '11px' : '13px', color: '#666' }}>
                                  Accuracy
                                </Text>
                              </div>
                            </div>
                          );
                        })()}
                      </Card>

                      {/* Detailed Results */}
                      {selectedTopic && (
                        <Card style={{
                          background: '#fff',
                          border: '1px solid #e9ecef',
                          borderRadius: '8px'
                        }}>
                          <Title level={4} style={{ 
                            fontSize: isMobile ? '14px' : '16px',
                            color: '#262626',
                            margin: '0 0 16px 0'
                          }}>
                            Quiz Answers Details
                          </Title>
                          
                          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {Array.isArray(filteredQuizData) && filteredQuizData.map((item: any, index: number) => (
                              <div key={index} style={{ 
                                marginBottom: '12px',
                                padding: '12px',
                                background: item.point > 0 ? '#f6ffed' : '#fff2f0',
                                borderRadius: '6px',
                                border: `1px solid ${item.point > 0 ? '#b7eb8f' : '#ffccc7'}`
                              }}>
                                <div style={{ marginBottom: '8px' }}>
                                  <Tag color={item.point > 0 ? 'success' : 'error'} style={{ marginBottom: '4px' }}>
                                    {item.point > 0 ? 'Correct' : 'Incorrect'}
                                  </Tag>
                                  <Tag color="blue">{item.lesson_title}</Tag>
                                </div>
                                
                                <div 
                                  dangerouslySetInnerHTML={{ __html: item.question_text }}
                                  style={{ 
                                    fontSize: isMobile ? '13px' : '14px',
                                    color: '#262626',
                                    marginBottom: '8px',
                                    lineHeight: '1.5',
                                    fontWeight: '600'
                                  }}
                                />
                                
                                {/* Answer Options */}
                                {item.answer_options && item.answer_options.length > 0 && (
                                  <div style={{ 
                                    marginTop: '12px',
                                    marginBottom: '12px',
                                    padding: '8px',
                                    background: 'white',
                                    borderRadius: '4px',
                                    border: '1px solid #e9ecef'
                                  }}>
                                    <Text strong style={{ 
                                      fontSize: isMobile ? '11px' : '12px',
                                      color: '#666',
                                      display: 'block',
                                      marginBottom: '6px'
                                    }}>
                                      Answer Options:
                                    </Text>
                                    <Space direction="vertical" size={6} style={{ width: '100%' }}>
                                      {item.answer_options
                                        .sort((a: any, b: any) => a.order_index - b.order_index)
                                        .map((option: any, optIdx: number) => (
                                          <div 
                                            key={option.id}
                                            style={{
                                              padding: '6px 10px',
                                              borderRadius: '4px',
                                              background: option.is_correct === 1 
                                                ? 'rgba(82, 196, 26, 0.1)' 
                                                : 'transparent',
                                              border: option.is_correct === 1 
                                                ? '1px solid #52c41a' 
                                                : '1px solid #e9ecef',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '8px'
                                            }}
                                          >
                                            <Text style={{ 
                                              fontSize: isMobile ? '12px' : '13px',
                                              color: '#262626',
                                              flex: 1
                                            }}>
                                              {option.option_text}
                                            </Text>
                                            {option.is_correct === 1 && (
                                              <Tag color="success" style={{ margin: 0, fontSize: '10px' }}>
                                                Correct
                                              </Tag>
                                            )}
                                          </div>
                                        ))}
                                    </Space>
                                  </div>
                                )}
                                
                                <div style={{ 
                                  fontSize: isMobile ? '11px' : '12px',
                                  color: '#666',
                                  display: 'flex',
                                  gap: '12px',
                                  flexWrap: 'wrap'
                                }}>
                                  <span>Question ID: {item.question_id}</span>
                                  <span>•</span>
                                  <span>Points: {item.point}</span>
                                  {item.exam_id && (
                                    <>
                                      <span>•</span>
                                      <span>Exam ID: {item.exam_id}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </Card>
                      )}
                    </div>


                  ) : (
                    <div style={{ 
                      textAlign: 'center',
                      padding: '40px',
                      color: '#666'
                    }}>
                      <Text>No quiz details available</Text>
                    </div>
                  )}
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;